import { Location } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { HeaderComponent } from './core/components/header/header.component';
import { NavbarComponent } from './core/components/navbar/navbar.component';
import { SideNavComponent } from './core/components/side-nav/side-nav.component';
import { NoInternetConnectionAlertComponent } from './shared/components/no-internet-connection-alert/no-internet-connection-alert.component';
import { AppUpdateService } from './core/services/app-updates/app-update.service';
import { AuthService } from './core/services/auth/auth.service';
import { IconCacheService } from './core/services/cache/icon-cache.service';
import { RefreshService } from './core/services/refresh/refresh.service';
import { ScreenService } from './core/services/responsive/screen.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, NavbarComponent, SideNavComponent, NoInternetConnectionAlertComponent],
})
export class AppComponent implements OnInit {
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  resizeService = inject(ScreenService);
  protected authService = inject(AuthService);
  protected updateService = inject(AppUpdateService);
  private readonly refreshService = inject(RefreshService);
  private readonly iconCache = inject(IconCacheService);
  private isLoggingOut = false;
  private readonly handleVisibilityChange = () => {
    if (typeof document !== 'undefined' && !document.hidden) {
      this.refreshService.refreshApp();
    }
  };

  constructor() {
    // Auth service initialization will automatically handle dispatching
    // both guardian and access loading actions when authenticated

    this.iconCache.preloadIcons([
      'close',
      'cog',
      'chevron-down',
      'chevron-up',
      'chevron-left',
      'chevron-right',
      'delete',
      'download',
      'filter',
      'edit',
      'email',
      'my-hub',
      'school-life-active',
      'school-life-inactive',
      'calendar-active',
      'calendar-inactive',
      'mail',
      'email-inactive',
      'hamburger',
      'cross',
      'logout',
    ]);
  }

  private initializeUpdateService(): void {
    // Check for updates on app startup
    setTimeout(() => {
      // enhanced logic: Do not interrupting the user with an update dialog
      // if they are in the middle of a critical flow like payments
      if (this.router.url.includes('payments') || this.router.url.includes('status=')) {
        return;
      }

      this.updateService.checkForUpdates();
    }, 3000);
  }

  ngOnInit(): void {
    if (Capacitor.isNativePlatform()) {
      CapacitorApp.addListener('backButton', () => {
        if (this.router.url.includes('home')) {
          CapacitorApp.exitApp();
        } else {
          this.location.back();
        }
      });

      // Handle deep links (e.g. curromyhub://payments/debit-orders) so SigniFlow
      // can redirect back into the native app on iOS after document signing.
      CapacitorApp.addListener('appUrlOpen', ({ url }) => {
        try {
          const path = new URL(url).pathname;
          if (path) this.router.navigateByUrl(path);
        } catch {
          // ignore malformed deep-link URLs
        }
      });

      if (Capacitor.getPlatform() === 'android') {
        StatusBar.setStyle({ style: Style.Light });
        StatusBar.setBackgroundColor({ color: '#ffffff' });
      }
    }

    CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this.refreshService.refreshApp();
      }
    });

    this.initializeUpdateService();

    window.addEventListener('storage', (event) => {
      if (event.key === 'logout-event' && !this.isLoggingOut) {
        this.isLoggingOut = true;
        this.authService.logout();
      }
    });
  }
}
